import React from 'react';

import { ATLAS_NO_LINK_ROUTES } from '@/components/navigation/constants';
import { useRouteDestination } from '@/components/navigation/hooks/use-route-destination';

import { ExternalNavLink } from './ExternalNavLink';
import { InternalNavLink } from './InternalNavLink';
import { NoLinkRoute } from './NoLinkRoute';

interface NavLinkProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  searchParams?: Record<string, string>;
  routeId: string;
}

/**
 * Smart navigation component that determines whether to use internal or external navigation
 * based on feature flags and migration configuration
 */
export const NavLink: React.FC<NavLinkProps> = ({ routeId, searchParams, children, onClick }) => {
  const { destination, url, routePath, params } = useRouteDestination(routeId, searchParams);

  // Handle non-navigable routes (Support, Help Center, etc.)
  if (ATLAS_NO_LINK_ROUTES.includes(routeId as any)) {
    return <NoLinkRoute onClick={onClick}>{children}</NoLinkRoute>;
  }

  // Render appropriate link component based on destination
  if (destination === 'gaia') {
    // If we have TanStack Router path and params, use them
    if (routePath && params) {
      return (
        <InternalNavLink
          routePath={routePath}
          params={params}
          searchParams={searchParams}
          onClick={onClick}
        >
          {children}
        </InternalNavLink>
      );
    }
    // If missing facility info, render as disabled
    return <div style={{ cursor: 'not-allowed', opacity: 0.5 }}>{children}</div>;
  }

  // External navigation to Darkwing
  if (url) {
    return (
      <ExternalNavLink href={url} onClick={onClick}>
        {children}
      </ExternalNavLink>
    );
  }

  // If no URL is available, render as disabled
  return <div style={{ cursor: 'not-allowed', opacity: 0.5 }}>{children}</div>;
};
